import React, { useState, useEffect, useRef, useCallback } from 'react'
import styles from './MyDocsTab.module.scss'
import DocTable from '../../components/DocTable/DocTable'
import SearchBar from '../../components/SearchBar/SearchBar'
import {
  getMyDocuments, uploadDocument, deleteDocument,
  downloadDocument, toggleDocumentFlag,
  getMyFolders, createMyFolder, updateMyFolder, deleteMyFolder,
} from 'service/KomurConnectionService'
import { fmtSize, fileIcon, filterDocs } from '../../utils'

// ── Цвета для выбора папок ───────────────────────────────────────────────────
const FOLDER_COLORS = [
  '#4338ca', '#2563eb', '#0891b2', '#059669',
  '#d97706', '#dc2626', '#7c3aed', '#db2777',
]

// ── Модал подтверждения ─────────────────────────────────────────────────────
function ConfirmModal({ title, message, onConfirm, onCancel }) {
  return (
    <div className={styles.confirmOverlay} onClick={onCancel}>
      <div className={styles.confirmBox} onClick={e => e.stopPropagation()}>
        <div className={styles.confirmIcon}>🗑️</div>
        <h4 className={styles.confirmTitle}>{title}</h4>
        {message && <p className={styles.confirmMsg}>{message}</p>}
        <div className={styles.confirmActions}>
          <button className={styles.confirmDeleteBtn} onClick={onConfirm}>Да, удалить</button>
          <button className={styles.confirmCancelBtn} onClick={onCancel}>Отмена</button>
        </div>
      </div>
    </div>
  )
}

// ── Карточка папки ───────────────────────────────────────────────────────────
function FolderCard({ folder, onClick, onDelete }) {
  return (
    <div
      className={styles.folderCard}
      style={{ '--fc': folder.color || '#4338ca' }}
    >
      <div
        className={styles.folderCardMain}
        onClick={() => onClick(folder)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && onClick(folder)}
      >
        <div className={styles.folderIconWrap}>
          <span className={styles.folderEmoji}>{folder.icon || '📁'}</span>
        </div>
        <div className={styles.folderInfo}>
          <span className={styles.folderName}>{folder.name}</span>
        </div>
        <div className={styles.folderRight}>
          <span className={styles.folderCount}>
            {folder.doc_count}{' '}
            {folder.doc_count === 1 ? 'документ' : folder.doc_count >= 2 && folder.doc_count <= 4 ? 'документа' : 'документов'}
          </span>
          <button
            className={styles.folderDeleteBtn}
            title="Удалить папку"
            onClick={e => { e.stopPropagation(); onDelete(folder) }}
          >
            <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13">
              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Форма загрузки файла ─────────────────────────────────────────────────────
function UploadForm({ pending, folderId, onConfirm, onCancel, uploading }) {
  const [title, setTitle] = useState(pending.title)
  const [desc,  setDesc]  = useState('')

  return (
    <div className={styles.form}>
      <div className={styles.formFile}>
        <span>{fileIcon(pending.file.name.split('.').pop())}</span>
        <span className={styles.formFilename}>{pending.file.name}</span>
        <span className={styles.formSize}>{fmtSize(pending.file.size)}</span>
      </div>
      <div className={styles.formFields}>
        <div className={styles.formField}>
          <label className={styles.formLabel}>Название документа</label>
          <input
            className={styles.formInput}
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Название документа"
            autoFocus
          />
        </div>
        <div className={styles.formField}>
          <label className={styles.formLabel}>
            Описание <span className={styles.formOptional}>(необязательно)</span>
          </label>
          <input
            className={styles.formInput}
            value={desc}
            onChange={e => setDesc(e.target.value)}
            placeholder="Краткое описание…"
          />
        </div>
      </div>
      <div className={styles.formActions}>
        <button
          className={styles.confirmBtn}
          onClick={() => onConfirm({ file: pending.file, title, desc, folderId })}
          disabled={uploading || !title.trim()}
        >
          {uploading ? '⏳ Загрузка…' : '✓ Загрузить'}
        </button>
        <button className={styles.cancelBtn} onClick={onCancel} disabled={uploading}>
          Отмена
        </button>
      </div>
    </div>
  )
}

// ── Модал создания папки ─────────────────────────────────────────────────────
function NewFolderModal({ onSave, onClose }) {
  const [name,  setName]  = useState('')
  const [icon,  setIcon]  = useState('📁')
  const [color, setColor] = useState(FOLDER_COLORS[0])
  const ICONS = ['📁','📂','📋','📌','🗂️','🔐','📊','📝']

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <span style={{ fontSize: 24 }}>{icon}</span>
          <h4 className={styles.modalTitle}>Новая папка</h4>
          <button className={styles.modalClose} onClick={onClose}>✕</button>
        </div>
        <div className={styles.formField} style={{ marginBottom: 14 }}>
          <label className={styles.formLabel}>Название</label>
          <input
            className={styles.formInput}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Название папки"
            autoFocus
          />
        </div>
        <div className={styles.formField} style={{ marginBottom: 14 }}>
          <label className={styles.formLabel}>Иконка</label>
          <div className={styles.iconPicker}>
            {ICONS.map(ic => (
              <button
                key={ic}
                className={`${styles.iconBtn} ${ic === icon ? styles.iconBtnActive : ''}`}
                onClick={() => setIcon(ic)}
              >{ic}</button>
            ))}
          </div>
        </div>
        <div className={styles.formField} style={{ marginBottom: 20 }}>
          <label className={styles.formLabel}>Цвет</label>
          <div className={styles.colorPicker}>
            {FOLDER_COLORS.map(c => (
              <button
                key={c}
                className={`${styles.colorBtn} ${c === color ? styles.colorBtnActive : ''}`}
                style={{ background: c }}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
        </div>
        <div className={styles.formActions}>
          <button
            className={styles.confirmBtn}
            onClick={() => name.trim() && onSave({ name: name.trim(), icon, color })}
            disabled={!name.trim()}
          >Создать</button>
          <button className={styles.cancelBtn} onClick={onClose}>Отмена</button>
        </div>
      </div>
    </div>
  )
}

// ── Основной компонент ───────────────────────────────────────────────────────
export default function MyDocsTab({ onPreview }) {
  const [allDocs,     setAllDocs]     = useState(null)   // все документы (для поиска)
  const [folders,     setFolders]     = useState(null)
  const [openFolder,  setOpenFolder]  = useState(null)   // папка открыта
  const [uploading,   setUploading]   = useState(false)
  const [uploadPending, setUploadPending] = useState(null) // { file, title }
  const [search,      setSearch]      = useState('')
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null) // { title, message, onConfirm }
  const fileInputRef = useRef()

  // ── Загрузка данных ────────────────────────────────────────────────────────
  const reloadAll = useCallback(() => {
    getMyDocuments().then(r => setAllDocs(r.data)).catch(() => setAllDocs([]))
    getMyFolders().then(r => setFolders(r.data)).catch(() => setFolders([]))
  }, [])

  useEffect(() => { reloadAll() }, [reloadAll])

  // ── Загрузка файла ─────────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadPending({ file, title: file.name.replace(/\.[^/.]+$/, '') })
    fileInputRef.current.value = ''
  }

  const handleUploadConfirm = async ({ file, title, desc, folderId }) => {
    if (!title.trim()) return
    const fd = new FormData()
    fd.append('file', file)
    fd.append('title', title.trim())
    if (desc?.trim()) fd.append('description', desc.trim())
    if (folderId) fd.append('folder', folderId)
    setUploading(true)
    try {
      const r = await uploadDocument(fd)
      setAllDocs(prev => [r.data, ...(prev || [])])
      // Обновить счётчик папки
      if (folderId) {
        setFolders(prev => prev?.map(f =>
          f.id === folderId ? { ...f, doc_count: (f.doc_count || 0) + 1 } : f
        ))
      }
      setUploadPending(null)
    } catch {}
    setUploading(false)
  }

  // ── Переключение флага ─────────────────────────────────────────────────────
  const handleToggle = (docId, flag) => {
    toggleDocumentFlag(docId, flag)
      .then(r => setAllDocs(prev => prev?.map(d => d.id === docId ? { ...d, ...r.data } : d)))
      .catch(() => {})
  }

  // ── Удаление документа ─────────────────────────────────────────────────────
  const handleDelete = (docId) => {
    const doc = allDocs?.find(d => d.id === docId)
    setConfirmModal({
      title: 'Удалить документ?',
      message: doc?.title ? `«${doc.title}» будет удалён без возможности восстановления.` : 'Документ будет удалён без возможности восстановления.',
      onConfirm: () => {
        setConfirmModal(null)
        deleteDocument(docId)
          .then(() => {
            setAllDocs(prev => prev?.filter(d => d.id !== docId))
            if (doc?.folder) {
              setFolders(prev => prev?.map(f =>
                f.id === doc.folder ? { ...f, doc_count: Math.max(0, (f.doc_count || 1) - 1) } : f
              ))
            }
          })
          .catch(() => {})
      },
    })
  }

  // ── Скачивание ─────────────────────────────────────────────────────────────
  const handleDownload = (_docType, id, fileName) => {
    downloadDocument(id).then(r => {
      const a = document.createElement('a')
      a.href = r.data.file_url
      a.download = fileName || 'document'
      a.click()
    }).catch(() => {})
  }

  // ── Папки ──────────────────────────────────────────────────────────────────
  const handleCreateFolder = async (data) => {
    try {
      const r = await createMyFolder(data)
      setFolders(prev => [...(prev || []), { ...r.data, doc_count: 0 }])
      setShowNewFolder(false)
    } catch {}
  }

  const handleDeleteFolder = (folder) => {
    setConfirmModal({
      title: `Удалить папку «${folder.name}»?`,
      message: 'Документы внутри не удалятся — они останутся без папки.',
      onConfirm: async () => {
        setConfirmModal(null)
        try {
          await deleteMyFolder(folder.id)
          setFolders(prev => prev?.filter(f => f.id !== folder.id))
          setAllDocs(prev => prev?.map(d => d.folder === folder.id ? { ...d, folder: null, folder_name: '' } : d))
          if (openFolder?.id === folder.id) setOpenFolder(null)
        } catch {}
      },
    })
  }

  // ── Данные для отображения ─────────────────────────────────────────────────
  const isSearching = search.trim().length > 0

  // Поиск по всем документам
  const searchResults = isSearching ? filterDocs(allDocs, search) : null

  // Документы без папки (для режима без поиска)
  const looseDocs = allDocs?.filter(d => !d.folder)

  // Документы открытой папки
  const folderDocs = openFolder
    ? allDocs?.filter(d => d.folder === openFolder.id) || []
    : null

  // ── Рендер: режим поиска ───────────────────────────────────────────────────
  if (isSearching) {
    return (
      <div className={styles.section}>
        <div className={styles.header}>
          <h3 className={styles.title}>Мои документы</h3>
        </div>
        <SearchBar value={search} onChange={setSearch} placeholder="Поиск по всем документам…" />
        <div className={styles.searchInfo}>
          Результаты поиска: <strong>{searchResults?.length ?? 0}</strong>
          {' '}(во всех папках и вне папок)
        </div>
        <DocTable
          docs={searchResults}
          showFlags
          onToggle={handleToggle}
          onDelete={handleDelete}
          onDownload={handleDownload}
          onPreview={onPreview}
        />
      </div>
    )
  }

  // ── Рендер: открытая папка ─────────────────────────────────────────────────
  if (openFolder) {
    return (
      <div className={styles.section}>
        <div className={styles.folderViewHeader}>
          <button className={styles.backBtn} onClick={() => setOpenFolder(null)}>
            ‹ Назад
          </button>
          <div className={styles.folderViewTitle}>
            <span style={{ color: openFolder.color, fontSize: 24 }}>{openFolder.icon}</span>
            <h3 className={styles.title}>{openFolder.name}</h3>
          </div>
          <button
            className={styles.uploadBtn}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            + Загрузить в папку
          </button>
        </div>

        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelect} />

        {uploadPending && (
          <UploadForm
            pending={uploadPending}
            folderId={openFolder.id}
            onConfirm={handleUploadConfirm}
            onCancel={() => setUploadPending(null)}
            uploading={uploading}
          />
        )}

        <SearchBar value={search} onChange={setSearch} placeholder="Поиск (ищет по всем документам)…" />
        <DocTable
          docs={folderDocs}
          showFlags
          onToggle={handleToggle}
          onDelete={handleDelete}
          onDownload={handleDownload}
          onPreview={onPreview}
        />
      </div>
    )
  }

  // ── Рендер: главный вид ────────────────────────────────────────────────────
  const hasFolders  = folders && folders.length > 0
  const hasLoose    = looseDocs && looseDocs.length > 0

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <h3 className={styles.title}>Мои документы</h3>
        <button className={styles.newFolderBtn} onClick={() => setShowNewFolder(true)}>
          📁 Новая папка
        </button>
        {!uploadPending && (
          <button
            className={styles.uploadBtn}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            + Загрузить файл
          </button>
        )}
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelect} />
      </div>

      {uploadPending && (
        <UploadForm
          pending={uploadPending}
          folderId={null}
          onConfirm={handleUploadConfirm}
          onCancel={() => setUploadPending(null)}
          uploading={uploading}
        />
      )}

      <SearchBar value={search} onChange={setSearch} placeholder="Поиск по всем документам…" />

      {/* Папки */}
      {folders === null
        ? <p className={styles.status}>Загрузка…</p>
        : hasFolders && (
          <div className={styles.foldersGrid}>
            {folders.map(folder => (
              <FolderCard
                key={folder.id}
                folder={folder}
                onClick={f => setOpenFolder(f)}
                onDelete={handleDeleteFolder}
              />
            ))}
          </div>
        )
      }

      {/* Документы без папки */}
      {hasLoose && (
        <div className={styles.looseSection}>
          {hasFolders && (
            <div className={styles.looseSectionHeader}>
              <span>📄</span>
              <span className={styles.looseSectionTitle}>Без папки</span>
              <span className={styles.looseSectionCount}>{looseDocs.length}</span>
            </div>
          )}
          <DocTable
            docs={looseDocs}
            showFlags
            onToggle={handleToggle}
            onDelete={handleDelete}
            onDownload={handleDownload}
            onPreview={onPreview}
          />
        </div>
      )}

      {!hasFolders && !hasLoose && folders !== null && allDocs !== null && (
        <p className={styles.status}>Нет документов. Загрузите первый файл.</p>
      )}

      {showNewFolder && (
        <NewFolderModal
          onSave={handleCreateFolder}
          onClose={() => setShowNewFolder(false)}
        />
      )}

      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}
    </div>
  )
}
